/**
 * 📜 JournalCodex — the player's NPC journal browser.
 *
 * Pulls /api/journals and renders the writer-by-writer feed of every
 * entry the player has unlocked through cinematics, quests, and world
 * events. NPCs without entries stay hidden ("undiscovered"), so the
 * page grows organically as the player plays.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MotherPhoenixFirstLetter from "@/components/MotherPhoenixFirstLetter";
import useMistyMap from "@/hooks/useMistyMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONE_TO_CLASSES = {
  amber:  "from-amber-950/40 to-black border-amber-700/40 text-amber-200",
  gold:   "from-yellow-950/40 to-black border-yellow-700/40 text-yellow-200",
  stone:  "from-stone-900/60 to-black border-stone-600/40 text-stone-200",
  ember:  "from-orange-950/40 to-black border-orange-700/40 text-orange-200",
  rose:   "from-rose-950/40 to-black border-rose-700/40 text-rose-200",
  cyan:   "from-cyan-950/40 to-black border-cyan-700/40 text-cyan-200",
  indigo: "from-indigo-950/40 to-black border-indigo-700/40 text-indigo-200",
  silver: "from-slate-900/60 to-black border-slate-500/40 text-slate-200",
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return ""; }
}

export default function JournalCodex() {
  useMistyMap("codex");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [activeNpc, setActiveNpc] = useState(null);
  const [firstLetter, setFirstLetter] = useState(null); // {should_show, letter}
  const [searchParams] = useSearchParams();
  const requestedNpc = searchParams.get("npc");

  useEffect(() => {
    axios.get(`${API}/journals`).then((r) => {
      setData(r.data);
      // Deep-link: ?npc=<id> takes priority over the default first entry
      const list = r.data?.npcs || [];
      if (list.length && !activeNpc) {
        const requested = requestedNpc && list.find((n) => n.id === requestedNpc);
        setActiveNpc(requested ? requested.id : list[0].id);
      }
    }).catch((e) => setErr(e.response?.data?.detail || "Failed to load Codex"));

    // 🔥👑 First Letter cinematic — only fires once per player, only if
    // Mother Phoenix's letter is already in their journal.
    axios.get(`${API}/journals/first-letter/state`)
      .then((r) => setFirstLetter(r.data))
      .catch(() => { /* silent — endpoint may 401 for guests */ });
  }, [requestedNpc]);

  const dismissFirstLetter = async () => {
    try { await axios.post(`${API}/journals/first-letter/dismiss`); } catch (_) { /* silent */ }
    setFirstLetter({ should_show: false });
  };

  const npc = (data?.npcs || []).find((n) => n.id === activeNpc) || (data?.npcs || [])[0];

  return (
    <div className="App">
      <Header />
      <div data-testid="journal-codex-page" className="min-h-screen bg-stone-950 text-amber-100 px-4 sm:px-6 py-8">
        <div className="max-w-6xl mx-auto">
          {/* 🔥👑 First Letter cinematic — fires once per player */}
          {firstLetter?.should_show && firstLetter?.letter && (
            <MotherPhoenixFirstLetter
              letter={firstLetter.letter}
              onDismiss={dismissFirstLetter}
            />
          )}

          {/* Header */}
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/70">
                📜 The Unbound&rsquo;s Codex
              </div>
              <h1 className="text-3xl sm:text-5xl font-black mt-1" style={{ fontFamily: "Anton, sans-serif" }}>
                JOURNAL OF DRAGONIOUS
              </h1>
              <p className="text-xs text-neutral-400 mt-2 italic max-w-2xl">
                Every voice that has written about you, one entry at a time. The Tomb remembers. So do they.
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black text-amber-200" data-testid="codex-total-entries">
                {data?.total_entries ?? 0}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">entries collected</div>
              <div className="text-[10px] text-neutral-600 mt-1">
                {data?.total_npcs_discovered ?? 0} / {data?.total_npcs_in_realm ?? 0} voices discovered
              </div>
            </div>
          </div>

          {err && (
            <div data-testid="codex-error" className="rounded-lg border border-red-700/60 bg-red-950/40 p-4 text-red-200 text-sm">
              {err}
            </div>
          )}

          {data && data.total_entries === 0 && (
            <div data-testid="codex-empty" className="rounded-xl border border-amber-700/30 bg-black/40 p-8 text-center mt-6">
              <div className="text-5xl mb-3">🪶</div>
              <div className="text-lg font-black text-amber-200">No one has written about you yet.</div>
              <div className="text-sm text-neutral-400 mt-2 max-w-md mx-auto">
                Complete the opening cinematic at the Tomb of Awakening and the NPCs of Dragonious will start recording what they see.
              </div>
              <Link
                to="/intro"
                data-testid="codex-empty-intro-link"
                className="inline-block mt-5 px-5 py-2 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-sm font-bold"
              >
                ✨ Begin the cinematic
              </Link>
            </div>
          )}

          {data && data.total_entries > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
              {/* NPC sidebar */}
              <aside className="space-y-2" data-testid="codex-npc-list">
                {data.npcs.map((n) => {
                  const isActive = n.id === activeNpc;
                  return (
                    <button
                      key={n.id}
                      data-testid={`codex-npc-${n.id}`}
                      onClick={() => setActiveNpc(n.id)}
                      className={`w-full text-left rounded-xl border-2 bg-gradient-to-br p-3 transition-all ${TONE_TO_CLASSES[n.tone] || TONE_TO_CLASSES.stone} ${isActive ? "ring-2 ring-amber-400/60 scale-[1.01]" : "opacity-80 hover:opacity-100"}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-3xl shrink-0">{n.icon}</div>
                        <div className="min-w-0">
                          <div className="font-black text-sm leading-tight" style={{ fontFamily: "Anton, sans-serif" }}>
                            {n.name}
                          </div>
                          <div className="text-[10px] text-neutral-400 mt-0.5 truncate">{n.tagline}</div>
                        </div>
                        <div className="ml-auto text-[10px] font-mono text-amber-300/80">×{n.entry_count}</div>
                      </div>
                    </button>
                  );
                })}
              </aside>

              {/* Entry feed */}
              <main data-testid="codex-entry-feed" className="space-y-4">
                {npc && (
                  <div className={`rounded-xl border-2 bg-gradient-to-br p-4 ${TONE_TO_CLASSES[npc.tone] || TONE_TO_CLASSES.stone}`}>
                    <div className="flex items-center gap-3">
                      <div className="text-5xl">{npc.icon}</div>
                      <div>
                        <div className="text-xs uppercase tracking-widest text-neutral-400">currently reading</div>
                        <div className="text-2xl font-black" style={{ fontFamily: "Anton, sans-serif" }}>{npc.name}</div>
                        <div className="text-xs text-neutral-300/70 italic mt-1">{npc.tagline}</div>
                      </div>
                    </div>
                  </div>
                )}

                {npc?.entries?.map((e, i) => (
                  <article
                    key={`${npc.id}-${i}`}
                    data-testid={`codex-entry-${npc.id}-${i}`}
                    className="rounded-xl border border-neutral-700/60 bg-black/60 p-4 backdrop-blur-sm"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="text-lg font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
                        {e.title}
                      </div>
                      <div className="text-[10px] text-neutral-500 shrink-0">{fmtDate(e.written_at)}</div>
                    </div>
                    <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-line">
                      {e.body}
                    </p>
                    {e.from_scene && (
                      <div className="mt-3 text-[10px] uppercase tracking-widest text-neutral-500">
                        recorded · {e.from_scene.replace(/_/g, " ")}
                      </div>
                    )}
                  </article>
                ))}
              </main>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
